import {
  CELL_MATCH_MODE_CONTAINS,
  CELL_MATCH_MODE_EQUALS,
  CELL_MATCH_MODE_NOT_CONTAINS,
  CELL_MATCH_MODE_NOT_EQUALS,
  normalizeCellMatchMode,
  valuesMatchByMode,
} from "./cellMatchModeUtils";
import { resolveSkipColumn } from "./mappingRulesUtils";

export const COLUMN_DEFAULT_MODE_ALWAYS = "always";

export const COLUMN_DEFAULT_MODE_OPTIONS = [
  { value: COLUMN_DEFAULT_MODE_ALWAYS, label: "ALWAYS" },
  { value: CELL_MATCH_MODE_EQUALS, label: "EQUALS" },
  { value: CELL_MATCH_MODE_NOT_EQUALS, label: "NOT" },
  { value: CELL_MATCH_MODE_CONTAINS, label: "CONTAINS" },
  { value: CELL_MATCH_MODE_NOT_CONTAINS, label: "NOT CONTAINS" },
];

const normalizeMode = (mode) => {
  const raw = String(mode || COLUMN_DEFAULT_MODE_ALWAYS).trim().toLowerCase();
  if (raw === COLUMN_DEFAULT_MODE_ALWAYS || raw === "default" || raw === "fallback") {
    return COLUMN_DEFAULT_MODE_ALWAYS;
  }
  return normalizeCellMatchMode(mode);
};

const normalizeRuleItem = (raw, targetField = null) => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const target = String(targetField || raw.target_field || raw.target || raw.field || "").trim();
  const value = raw.value == null ? "" : String(raw.value).trim();
  if (!target || !value) {
    return null;
  }
  const mode = normalizeMode(raw.mode);
  const feedColumn = String(raw.feed_column || raw.column || "").trim();
  if (mode !== COLUMN_DEFAULT_MODE_ALWAYS && !feedColumn) {
    return null;
  }
  return {
    feed_column: feedColumn,
    target_field: target,
    mode,
    match: raw.match == null ? "" : String(raw.match),
    value,
  };
};

const sortTargetRules = (rules) => {
  const conditional = rules.filter((rule) => rule.mode !== COLUMN_DEFAULT_MODE_ALWAYS);
  const alwaysRules = rules.filter((rule) => rule.mode === COLUMN_DEFAULT_MODE_ALWAYS);
  return [...conditional, ...(alwaysRules.length ? [alwaysRules[0]] : [])];
};

export const normalizeColumnDefaultGroups = (raw) => {
  if (!raw) {
    return {};
  }
  const grouped = {};

  if (Array.isArray(raw)) {
    raw.forEach((item) => {
      const rule = normalizeRuleItem(item);
      if (!rule) {
        return;
      }
      if (!grouped[rule.target_field]) {
        grouped[rule.target_field] = [];
      }
      grouped[rule.target_field].push(rule);
    });
  } else if (typeof raw === "object") {
    Object.entries(raw).forEach(([targetField, rulesRaw]) => {
      if (!Array.isArray(rulesRaw)) {
        return;
      }
      const rules = rulesRaw
        .map((item) => normalizeRuleItem(item, targetField))
        .filter(Boolean);
      if (rules.length) {
        grouped[String(targetField).trim()] = sortTargetRules(rules);
      }
    });
  }

  return Object.fromEntries(
    Object.keys(grouped).sort().map((target) => [target, grouped[target]]),
  );
};

export const flattenColumnDefaultGroups = (groups) => (
  Object.entries(normalizeColumnDefaultGroups(groups)).flatMap(([targetField, rules]) => (
    rules.map((rule) => ({ ...rule, target_field: targetField }))
  ))
);

export const serializeColumnDefaultGroups = (groups) => {
  const normalized = normalizeColumnDefaultGroups(groups);
  return Object.fromEntries(
    Object.entries(normalized).map(([target, rules]) => [
      target,
      rules.map(({ feed_column: feedColumn, mode, match, value }) => ({
        feed_column: feedColumn,
        mode,
        match,
        value,
      })),
    ]),
  );
};

export const ruleMatchesRow = (row, rule, headerLabels = null) => {
  if (!row || !rule) {
    return false;
  }
  if (rule.mode === COLUMN_DEFAULT_MODE_ALWAYS) {
    return true;
  }
  const column = resolveSkipColumn(rule.feed_column, headerLabels);
  if (!column) {
    return false;
  }
  return valuesMatchByMode(row[column], rule.match, rule.mode);
};

export const resolveWinningRule = (row, rules, headerLabels = null) => {
  const normalized = sortTargetRules(rules || []);
  const conditional = normalized.filter((rule) => rule.mode !== COLUMN_DEFAULT_MODE_ALWAYS);
  const alwaysRules = normalized.filter((rule) => rule.mode === COLUMN_DEFAULT_MODE_ALWAYS);
  const matchedConditional = conditional.find((rule) => ruleMatchesRow(row, rule, headerLabels));
  if (matchedConditional) {
    return matchedConditional;
  }
  if (alwaysRules.length) {
    return alwaysRules[0];
  }
  return null;
};

const mappedFeedColumnForTarget = (targetField, columnMapping = {}) => {
  const entry = Object.entries(columnMapping || {}).find(([, mappedField]) => mappedField === targetField);
  return entry ? String(entry[0]) : "";
};

export const resolveHighlightFeedColumn = (rule, targetField, columnMapping = {}, headerLabels = null) => {
  if (!rule) {
    return "";
  }
  if (rule.mode !== COLUMN_DEFAULT_MODE_ALWAYS) {
    return resolveSkipColumn(rule.feed_column, headerLabels);
  }
  const explicit = resolveSkipColumn(rule.feed_column, headerLabels);
  if (explicit) {
    return explicit;
  }
  return mappedFeedColumnForTarget(targetField, columnMapping);
};

export const resolveColumnDefaultOverlays = (row, columnDefaults, headerLabels = null, columnMapping = {}) => {
  const grouped = normalizeColumnDefaultGroups(columnDefaults);
  const overlays = {};
  Object.entries(grouped).forEach(([targetField, rules]) => {
    const winning = resolveWinningRule(row, rules, headerLabels);
    if (!winning) {
      return;
    }
    const column = resolveHighlightFeedColumn(winning, targetField, columnMapping, headerLabels);
    if (column) {
      overlays[column] = `${targetField}->${winning.value}`;
    }
  });
  return overlays;
};

export const rowMatchesColumnDefaultRules = (row, columnDefaults, headerLabels = null, columnMapping = {}) => (
  Object.keys(resolveColumnDefaultOverlays(row, columnDefaults, headerLabels, columnMapping)).length > 0
);

export const formatColumnDefaultModeLabel = (mode) => (
  COLUMN_DEFAULT_MODE_OPTIONS.find((option) => option.value === normalizeMode(mode))?.label
  || String(mode || COLUMN_DEFAULT_MODE_ALWAYS).toUpperCase()
);

export const formatColumnDefaultRuleCardLabel = (rule, targetField, { feedColumnLabel = "" } = {}) => {
  if (!rule) {
    return "";
  }
  const target = String(targetField || rule.target_field || "").trim();
  const appliedValue = String(rule.value ?? "");
  if (normalizeMode(rule.mode) === COLUMN_DEFAULT_MODE_ALWAYS) {
    return `ALWAYS => ${target} = ${appliedValue}`;
  }
  const feedLabel = String(feedColumnLabel || rule.feed_column || "").trim() || "?";
  const modeLabel = formatColumnDefaultModeLabel(rule.mode);
  const matchRaw = String(rule.match ?? "").trim();
  const matchDisplay = matchRaw === "" ? "(vuoto/null)" : matchRaw;
  return `${feedLabel} ${modeLabel} ${matchDisplay} => ${target} = ${appliedValue}`;
};

export const addRuleToGroups = (groups, rule) => {
  const normalizedRule = normalizeRuleItem(rule);
  if (!normalizedRule) {
    return groups;
  }
  const next = normalizeColumnDefaultGroups(groups);
  const targetRules = [...(next[normalizedRule.target_field] || []), normalizedRule];
  next[normalizedRule.target_field] = sortTargetRules(targetRules);
  return next;
};

export const removeRuleFromGroups = (groups, targetField, index) => {
  const next = normalizeColumnDefaultGroups(groups);
  const rules = [...(next[targetField] || [])];
  rules.splice(index, 1);
  if (rules.length) {
    next[targetField] = rules;
  } else {
    delete next[targetField];
  }
  return next;
};

export const hasConditionalColumnDefaultRules = (groups) => (
  flattenColumnDefaultGroups(groups).some((rule) => rule.mode !== COLUMN_DEFAULT_MODE_ALWAYS)
);
